import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Crown, Save, Plus, Pin, Trash2, Loader2, UserPlus, X, ExternalLink, Mail } from 'lucide-react';
import { WitnessCirclesAdmin, SeasonsAdmin } from '@/components/admin/AdminLegacyPhase4';
import { EventsAdmin } from '@/components/admin/AdminLegacyEvents';
import { EngagementAdmin } from '@/components/admin/AdminLegacyEngagement';
import { DateField, TimeField } from '@/components/legacy/PrettyPickers';

const API_URL = window.location.origin;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SEASON_OPTIONS = [
  { value: '1', label: 'Season 1: Remember (Months 1–3)' },
  { value: '2', label: 'Season 2: Return (Months 4–6)' },
  { value: '3', label: 'Season 3: Build (Months 7–9)' },
  { value: '4', label: 'Season 4: Multiply (Months 10–12)' }
];

const GatheringFields = ({ label, value, onChange, testId }) => (
  <div className="border border-sand rounded-xl p-4 space-y-3">
    <p className="font-medium text-forest text-sm">{label}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs">Title</Label>
        <Input value={value?.title || ''} onChange={(e) => onChange({ ...value, title: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">Date (e.g., August 14, 2026)</Label>
        <DateField value={value?.date} onChange={(v) => onChange({ ...value, date: v })} testId={`${testId}-date`} />
      </div>
      <div>
        <Label className="text-xs">Time (e.g., 7:00 PM)</Label>
        <TimeField value={value?.time} onChange={(v) => onChange({ ...value, time: v })} testId={`${testId}-time`} />
      </div>
      <div>
        <Label className="text-xs">Time Zone</Label>
        <Input value={value?.timezone || ''} onChange={(e) => onChange({ ...value, timezone: e.target.value })} placeholder="ET" />
      </div>
    </div>
    <div>
      <Label className="text-xs">Zoom Link</Label>
      <Input value={value?.zoom_link || ''} onChange={(e) => onChange({ ...value, zoom_link: e.target.value })} placeholder="https://zoom.us/j/..." />
    </div>
    <div>
      <Label className="text-xs">Description (optional)</Label>
      <Input value={value?.description || ''} onChange={(e) => onChange({ ...value, description: e.target.value })} />
    </div>
  </div>
);

const EXAMPLE_PROMPTS = [
  'What truth are you avoiding?',
  'Where did you choose yourself this week?',
  'Where did you abandon yourself last week?',
  'What are you building this week?',
  'What are you releasing?',
  'What would change if you trusted yourself?',
  'What are you carrying that no longer belongs to you?',
  'Where are you performing instead of being present?',
  'What does your future self need from you this week?'
];

export const AdminLegacyTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [members, setMembers] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', pinned: false, comments_enabled: true });
  const [creating, setCreating] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [newPrompt, setNewPrompt] = useState({ channel: 'reflection', text: '', scheduled_date: '' });
  const [addingPrompt, setAddingPrompt] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingPulse, setSendingPulse] = useState(false);

  const handleTestPulseReminder = async () => {
    setSendingPulse(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/legacy/pulse-reminder/test`, {}, authHeaders());
      toast.success(`Preview pulse reminder sent to ${res.data.to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test reminder');
    } finally {
      setSendingPulse(false);
    }
  };

  const handleTestDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/legacy/digest/test`, {}, authHeaders());
      const d = res.data;
      toast.success(`Preview digest sent to ${d.to} — ${d.wins} wins, ${d.witnesses} witnesses, ${d.prayers} prayers`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const fetchPrompts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/legacy/prompts`, authHeaders());
      setPrompts(res.data);
    } catch { /* noop */ }
  };

  const handleAddPrompt = async () => {
    if (!newPrompt.text.trim() || !newPrompt.scheduled_date) {
      toast.error('Prompt text and date are required');
      return;
    }
    setAddingPrompt(true);
    try {
      await axios.post(`${API_URL}/api/admin/legacy/prompts`, newPrompt, authHeaders());
      toast.success('Prompt scheduled');
      setNewPrompt({ ...newPrompt, text: '', scheduled_date: '' });
      fetchPrompts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule prompt');
    } finally {
      setAddingPrompt(false);
    }
  };

  const handleDeletePrompt = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/legacy/prompts/${id}`, authHeaders());
      fetchPrompts();
    } catch {
      toast.error('Failed to delete prompt');
    }
  };

  const handlePublishPrompt = async (id) => {
    if (!window.confirm('Publish this prompt now? All members will be notified.')) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/legacy/prompts/${id}/publish`, {}, authHeaders());
      toast.success(`Published — ${res.data.members_notified} members notified`);
      fetchPrompts();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    }
  };

  const fetchAll = async () => {
    try {
      const [s, a, m] = await Promise.all([
        axios.get(`${API_URL}/api/admin/legacy/settings`, authHeaders()),
        axios.get(`${API_URL}/api/legacy/announcements`, authHeaders()),
        axios.get(`${API_URL}/api/admin/legacy/members`, authHeaders())
      ]);
      setSettings(s.data);
      setAnnouncements(a.data);
      setMembers(m.data);
    } catch (error) {
      toast.error('Failed to load Legacy Circle data');
    }
  };

  useEffect(() => { fetchAll(); fetchPrompts(); }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        welcome_message: settings.welcome_message,
        welcome_video_url: settings.welcome_video_url,
        current_season: settings.current_season,
        monthly_theme: settings.monthly_theme,
        current_reflection: settings.current_reflection,
        next_gathering: settings.next_gathering,
        next_witness_circle: settings.next_witness_circle,
        covenant_link: settings.covenant_link
      };
      const res = await axios.put(`${API_URL}/api/admin/legacy/settings`, payload, authHeaders());
      setSettings(res.data);
      toast.success('Legacy Circle settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/legacy/announcements`, newAnnouncement, authHeaders());
      toast.success('Announcement posted');
      setNewAnnouncement({ title: '', content: '', pinned: false, comments_enabled: true });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post announcement');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateAnnouncement = async (id, update) => {
    try {
      await axios.put(`${API_URL}/api/admin/legacy/announcements/${id}`, update, authHeaders());
      fetchAll();
    } catch {
      toast.error('Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/legacy/announcements/${id}`, authHeaders());
      toast.success('Announcement deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete announcement');
    }
  };

  const handleSearchUsers = async (q) => {
    setMemberSearch(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/admin/users?search=${encodeURIComponent(q)}&limit=8`, authHeaders());
      const users = Array.isArray(res.data) ? res.data : (res.data.users || []);
      const memberIds = new Set(members.map(m => m.id));
      setSearchResults(users.filter(u => !memberIds.has(u.id)).slice(0, 8));
    } catch {
      setSearchResults([]);
    }
  };

  const handleAddMember = async (user) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/legacy/members`, { user_id: user.id }, authHeaders());
      const emailNote = res.data?.email_sent ? ' — invitation email sent ✉️' : '';
      toast.success(`${user.full_name || user.email} added to the Legacy Circle${emailNote}`);
      setMemberSearch('');
      setSearchResults([]);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.full_name || member.email} from the Legacy Circle?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/legacy/members/${member.id}`, authHeaders());
      toast.success('Member removed');
      fetchAll();
    } catch {
      toast.error('Failed to remove member');
    }
  };

  if (!settings) {
    return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-sand rounded-xl animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-legacy-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest flex items-center gap-2">
            <Crown className="h-6 w-6 text-terracotta" /> Legacy Circle
          </h2>
          <p className="text-sm text-gray-500">Manage the private year-long Legacy Circle space.</p>
        </div>
        <a href="/legacy" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm"><ExternalLink className="h-4 w-4 mr-2" /> View Member Space</Button>
        </a>
      </div>

      {/* Sunday Digest + Pulse Reminder */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-heading font-semibold text-forest flex items-center gap-2"><Mail className="h-4 w-4 text-terracotta" /> Sunday Circle Digest</p>
              <p className="text-sm text-gray-500 mt-1">Every Sunday at 6 PM ET, members receive a recap of the week's wins, witnesses, and prayers. Quiet weeks are skipped automatically.</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleTestDigest} disabled={sendingDigest} data-testid="send-test-digest-btn">
              {sendingDigest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />} Send Test Digest to Me
            </Button>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap border-t border-sand pt-4">
            <div>
              <p className="font-heading font-semibold text-forest flex items-center gap-2"><Mail className="h-4 w-4 text-terracotta" /> Monthly Pulse Reminder</p>
              <p className="text-sm text-gray-500 mt-1">On the 1st of each month at 9 AM ET, each sister gets a 30-second nudge to update her Journey Growth & Confidence. Sisters who updated in the past week are skipped.</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleTestPulseReminder} disabled={sendingPulse} data-testid="send-test-pulse-btn">
              {sendingPulse ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />} Send Test Reminder to Me
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader><CardTitle className="text-lg font-heading text-forest">Space Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Current Season</Label>
              <Select value={String(settings.current_season || 1)} onValueChange={(v) => setSettings({ ...settings, current_season: parseInt(v) })}>
                <SelectTrigger data-testid="legacy-season-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEASON_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monthly Theme</Label>
              <Input value={settings.monthly_theme || ''} onChange={(e) => setSettings({ ...settings, monthly_theme: e.target.value })} placeholder="e.g., Original Design" data-testid="legacy-monthly-theme-input" />
            </div>
          </div>
          <div>
            <Label>Welcome Message (shown on Home)</Label>
            <Textarea value={settings.welcome_message || ''} onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })} rows={3} data-testid="legacy-welcome-message-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Welcome Video URL (embed link)</Label>
              <Input value={settings.welcome_video_url || ''} onChange={(e) => setSettings({ ...settings, welcome_video_url: e.target.value })} placeholder="https://player.vimeo.com/video/..." data-testid="legacy-welcome-video-input" />
            </div>
            <div>
              <Label>Founders Covenant Link</Label>
              <Input value={settings.covenant_link || ''} onChange={(e) => setSettings({ ...settings, covenant_link: e.target.value })} placeholder="https://..." />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Current Monday Reflection</Label>
              <Input
                value={settings.current_reflection?.question || ''}
                onChange={(e) => setSettings({ ...settings, current_reflection: { ...settings.current_reflection, question: e.target.value } })}
                placeholder="What truth are you avoiding?"
                data-testid="legacy-reflection-input"
              />
            </div>
            <div>
              <Label>Week Of (e.g., July 20)</Label>
              <DateField format="monthday" testId="week-of"
                value={settings.current_reflection?.week_of}
                onChange={(v) => setSettings({ ...settings, current_reflection: { ...settings.current_reflection, week_of: v } })}
              />
            </div>
          </div>
          <GatheringFields label="Next Legacy Circle Gathering (full group)" testId="gathering" value={settings.next_gathering} onChange={(v) => setSettings({ ...settings, next_gathering: v })} />
          <GatheringFields label="Next Witness Circle Meeting (small group)" testId="witness-gathering" value={settings.next_witness_circle} onChange={(v) => setSettings({ ...settings, next_witness_circle: v })} />
          <Button onClick={handleSaveSettings} disabled={saving} className="bg-forest hover:bg-forest/90 text-white" data-testid="legacy-save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Settings
          </Button>
        </CardContent>
      </Card>

      {/* Weekly Rhythm Prompts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading text-forest">Weekly Rhythm — Prompt Schedule</CardTitle>
          <p className="text-sm text-gray-500">Queue Monday Reflection questions in advance. Prompts publish automatically on their date (~7 AM ET) and notify all members. Wednesday Wins &amp; Friday Wisdom reminders go out automatically; schedule a custom prompt here to override.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border border-sand rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Channel</Label>
                <Select value={newPrompt.channel} onValueChange={(v) => setNewPrompt({ ...newPrompt, channel: v })}>
                  <SelectTrigger data-testid="prompt-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reflection">Monday Reflection</SelectItem>
                    <SelectItem value="wins">Celebrate Wednesday</SelectItem>
                    <SelectItem value="wisdom">Friday Wisdom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Publish Date</Label>
                <Input type="date" value={newPrompt.scheduled_date} onChange={(e) => setNewPrompt({ ...newPrompt, scheduled_date: e.target.value })} data-testid="prompt-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Prompt</Label>
              <Textarea value={newPrompt.text} onChange={(e) => setNewPrompt({ ...newPrompt, text: e.target.value })} rows={2} placeholder="What truth are you avoiding?" data-testid="prompt-text-input" />
              <div className="flex flex-wrap gap-1 mt-2">
                {EXAMPLE_PROMPTS.slice(0, 5).map(p => (
                  <button key={p} onClick={() => setNewPrompt({ ...newPrompt, text: p })} className="text-xs px-2 py-1 rounded-full bg-sand/70 text-gray-600 hover:bg-sand">
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={handleAddPrompt} disabled={addingPrompt} className="bg-forest hover:bg-forest/90 text-white" data-testid="schedule-prompt-btn">
              {addingPrompt ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Schedule Prompt
            </Button>
          </div>
          <div className="space-y-2">
            {prompts.map(p => (
              <div key={p.id} className="flex items-center justify-between gap-3 border border-sand rounded-xl p-3" data-testid={`admin-prompt-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">{p.channel}</Badge>
                    <span className="text-xs text-gray-400">{p.scheduled_date}</span>
                    {p.published && <Badge className="bg-green-100 text-green-700 text-xs">Published</Badge>}
                  </div>
                  <p className="text-sm text-forest mt-1 truncate">{p.text}</p>
                </div>
                {!p.published && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handlePublishPrompt(p.id)} data-testid={`publish-prompt-${p.id}`}>Publish Now</Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeletePrompt(p.id)} data-testid={`delete-prompt-${p.id}`}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {prompts.length === 0 && <p className="text-sm text-gray-400 text-center py-2">No prompts scheduled yet.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Witness Circles + Seasons (Phase 4) */}
      <WitnessCirclesAdmin legacyMembers={members} />
      <EventsAdmin />
      <EngagementAdmin />
      <SeasonsAdmin />

      {/* Announcements */}
      <Card>
        <CardHeader><CardTitle className="text-lg font-heading text-forest">Announcements</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="border border-sand rounded-xl p-4 space-y-3">
            <Input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })} placeholder="Announcement title" data-testid="legacy-announcement-title-input" />
            <Textarea value={newAnnouncement.content} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })} placeholder="Write your announcement..." rows={3} data-testid="legacy-announcement-content-input" />
            <div className="flex items-center gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <Switch checked={newAnnouncement.pinned} onCheckedChange={(v) => setNewAnnouncement({ ...newAnnouncement, pinned: v })} /> Pin to top
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <Switch checked={newAnnouncement.comments_enabled} onCheckedChange={(v) => setNewAnnouncement({ ...newAnnouncement, comments_enabled: v })} /> Allow comments
              </label>
              <Button onClick={handleCreateAnnouncement} disabled={creating} className="bg-forest hover:bg-forest/90 text-white ml-auto" data-testid="legacy-post-announcement-btn">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Post Announcement
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {announcements.map(a => (
              <div key={a.id} className="flex items-start justify-between gap-3 border border-sand rounded-xl p-3" data-testid={`admin-announcement-${a.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {a.pinned && <Badge className="bg-terracotta/10 text-terracotta text-xs"><Pin className="h-3 w-3 mr-1" />Pinned</Badge>}
                    {a.comments_enabled === false && <Badge variant="outline" className="text-xs">Comments off</Badge>}
                    <p className="font-medium text-forest truncate">{a.title}</p>
                  </div>
                  <p className="text-xs text-gray-400">{new Date(a.created_at).toLocaleDateString()} · {(a.reactions || []).length} reactions · {(a.comments || []).length} comments</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => handleUpdateAnnouncement(a.id, { pinned: !a.pinned })} title={a.pinned ? 'Unpin' : 'Pin'} data-testid={`admin-pin-toggle-${a.id}`}>
                    <Pin className={`h-4 w-4 ${a.pinned ? 'text-terracotta' : 'text-gray-400'}`} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleUpdateAnnouncement(a.id, { comments_enabled: a.comments_enabled === false })} title="Toggle comments">
                    <span className={`text-xs ${a.comments_enabled === false ? 'text-gray-400' : 'text-forest'}`}>💬</span>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteAnnouncement(a.id)} data-testid={`admin-delete-announcement-${a.id}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
            {announcements.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No announcements yet.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading text-forest">Members ({members.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Input value={memberSearch} onChange={(e) => handleSearchUsers(e.target.value)} placeholder="Search users by name or email to add..." data-testid="legacy-member-search-input" />
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-sand rounded-xl shadow-lg z-20 max-h-64 overflow-y-auto">
                {searchResults.map(u => (
                  <button key={u.id} onClick={() => handleAddMember(u)} className="w-full flex items-center gap-3 p-3 hover:bg-sand/50 text-left" data-testid={`legacy-add-member-${u.id}`}>
                    <UserPlus className="h-4 w-4 text-forest flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-forest truncate">{u.full_name || 'Unnamed'}</p>
                      <p className="text-xs text-gray-400 truncate">{u.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {members.map(m => (
              <div key={m.id} className="flex items-center justify-between gap-2 border border-sand rounded-xl p-3" data-testid={`legacy-member-${m.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={m.profile_image || undefined} />
                    <AvatarFallback className="bg-forest text-white text-xs">{(m.full_name || m.email || '?').charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-forest truncate">{m.full_name || 'Unnamed'}</p>
                    <p className="text-xs text-gray-400 truncate">{m.email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(m)} data-testid={`legacy-remove-member-${m.id}`}>
                  <X className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            ))}
          </div>
          {members.length === 0 && <p className="text-sm text-gray-400 text-center py-2">No members yet — search above to add the first women.</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLegacyTab;
